import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchSites } from 'store/sitesSlice';
import { fetchProviders } from 'store/paymentProvidersSlice';
import { fetchWithdrawTransactions, initialParams } from 'store/transactionsSlice';
import { RootState } from 'store/index';
import { commonGridStyles } from 'styles/gridStyles';
import commonStyles from 'styles/common.module.scss';
import { formatDate } from 'utils/date';
import dayjs, { Dayjs } from 'dayjs';
import RangeDatePicker from 'components/common/RangeDatePicker/RangeDatePicker';
import CustomPagination from 'components/common/pagination/pagination';
import CopyableId from 'components/common/CopyableId/CopyableId';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/pagination';
import { POLLING_INTERVAL } from 'constants/config';
import styles from './Withdraw.module.scss';

const STATUSES = ['Processing', 'Completed', 'Pending', 'Cancelled', 'Failed'];

const Withdraw: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: sites, isLoading: sitesLoading } = useAppSelector(
    (state: RootState) => state.sites,
  );
  const { data: providers, isLoading: providersLoading } = useAppSelector(
    (state: RootState) => state.paymentProviders,
  );
  const { data, total, isLoading } = useAppSelector((state) => state.transactions.withdraw);

  const [params, setParams] = useState(initialParams);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const fromDate = params.dateFrom ? dayjs(params.dateFrom) : null;
  const toDate = params.dateTo ? dayjs(params.dateTo) : null;

  useEffect(() => {
    if (sites.length === 0) {
      dispatch(fetchSites({ page: 1, limit: 100, sortField: 'id', sortValue: 'ASC' }));
    }
  }, [dispatch, sites.length]);

  useEffect(() => {
    if (providers.length === 0) {
      dispatch(fetchProviders());
    }
  }, [dispatch, providers.length]);

  useEffect(() => {
    if (sites.length > 0 && isInitialLoad) {
      const defaultSite = sites.find((s) => s.title.toLowerCase().includes('dgenta')) || sites[0];
      if (defaultSite) {
        setParams((prev) => ({ ...prev, siteId: defaultSite.id }));
        setIsInitialLoad(false);
      }
    }
  }, [sites, isInitialLoad]);

  const loadData = useCallback(() => {
    if (!isInitialLoad && params.siteId !== 'all') {
      dispatch(fetchWithdrawTransactions(params));
    }
  }, [dispatch, params, isInitialLoad]);

  useEffect(() => {
    loadData();
    const interval = setInterval(() => {
      if (!isInitialLoad && params.siteId !== 'all') {
        dispatch(fetchWithdrawTransactions({ ...params, isBackground: true }));
      }
    }, POLLING_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData, isInitialLoad, params, dispatch]);

  const handleSiteChange = (siteId: string | number) => {
    setParams((prev) => ({ ...prev, siteId, page: 1 }));
  };

  const handleStatusChange = (status: string) => {
    setParams((prev) => ({ ...prev, status, page: 1 }));
  };

  const handleProviderChange = (providerId: string | number) => {
    setParams((prev) => ({ ...prev, providerId, page: 1 }));
  };

  const handleDateChange = (from: Dayjs | null, to: Dayjs | null) => {
    setParams((prev) => ({
      ...prev,
      dateFrom: from ? from.toISOString() : null,
      dateTo: to ? to.toISOString() : null,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setParams((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (limit: number) => {
    setParams((prev) => ({ ...prev, limit, page: 1 }));
  };

  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      setParams((prev) => ({
        ...prev,
        sortField: model[0].field,
        sortValue: model[0].sort?.toUpperCase() as 'ASC' | 'DESC',
      }));
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'externalTxId',
      headerName: 'External Tx Id',
      width: 300,
      renderCell: (params) => <CopyableId value={params.value} label="external transaction id" />,
    },
    {
      field: 'user',
      headerName: 'User',
      width: 150,
      renderCell: (params) => (
        <span
          className={styles.userLink}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/clients/${params.row.user?.id || params.row.userId}`);
          }}
        >
          {params.row.user?.username || params.value || '-'}
        </span>
      ),
    },
    {
      field: 'cryptoName',
      headerName: 'Method',
      width: 200,
      renderCell: (params) => (
        <>
          {params.row?.methodImageUrl && (
            <img
              style={{ marginRight: '6px' }}
              width={20}
              src={params.row.methodImageUrl}
              alt={
                params.row.cryptoName || params.row.paymentSystem || params.row.system || 'Method'
              }
            />
          )}
          <Typography fontSize={12}>
            {params.row.cryptoName || params.row.paymentSystem || params.row.system || '-'}
          </Typography>
        </>
      ),
    },
    {
      field: 'provider',
      headerName: 'Provider name',
      width: 200,
      renderCell: (params) => params.row?.provider?.name || '-',
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 150,
      renderCell: (params) => (
        <Typography className={styles.amount}>
          {' '}
          $ {Number(params.value).toLocaleString()}{' '}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (params) => (
        <span className={`${styles.status} ${styles[params.value?.toLowerCase() as string]}`}>
          {params.value ? params.value.charAt(0).toUpperCase() + params.value.slice(1) : '-'}
        </span>
      ),
    },
    {
      field: 'site',
      headerName: 'Site',
      width: 120,
      renderCell: (params) => params.row.site?.name || params.row.site?.title || '-',
    },
    {
      field: 'createdAt',
      headerName: 'Date',
      width: 200,
      renderCell: (params) => formatDate(params.value || params.row.date),
    },
  ];

  const renderPagination = () => (
    <CustomPagination
      count={total || 0}
      page={params.page - 1}
      onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
      rowsPerPage={params.limit}
      onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    />
  );

  return (
    <Box className={styles.withdrawContainer}>
      <Box className={styles.header}>
        <Typography variant="h4" className={styles.title}>
          Withdrawal History
        </Typography>
        <Box className={styles.controls}>
          <Box className={styles.filterItem}>
            <RangeDatePicker
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={(val) => handleDateChange(val, toDate)}
              onToChange={(val) => handleDateChange(fromDate, val)}
            />
          </Box>
          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Site</InputLabel>
              <Select
                value={params.siteId === 'all' ? '' : params.siteId}
                label="Site"
                onChange={(e) => handleSiteChange(e.target.value as string | number)}
                disabled={sitesLoading}
              >
                {sites.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Status</InputLabel>
              <Select
                value={params.status || 'all'}
                label="Status"
                onChange={(e) => handleStatusChange(e.target.value.toLowerCase())}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                {STATUSES.map((status) => (
                  <MenuItem key={status} value={status.toLowerCase()}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Provider</InputLabel>
              <Select
                value={params.providerId || 'all'}
                label="Provider"
                onChange={(e) => handleProviderChange(e.target.value)}
                disabled={providersLoading}
              >
                <MenuItem value="all">All Providers</MenuItem>
                {providers.map((provider) => (
                  <MenuItem key={provider.code} value={provider.id}>
                    {provider.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={data}
          loading={isLoading}
          columns={columns}
          rowCount={total}
          onSortModelChange={handleSortModelChange}
          sx={commonGridStyles}
        />
        {renderPagination()}
      </Box>
    </Box>
  );
};

export default Withdraw;
